import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BadgeComponent, ButtonDirective, CardBodyComponent, CardComponent, SpinnerComponent } from '@coreui/angular-pro';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';

import {
  FamilyInvitation, FamilyInvitationStatus, FamilyOverview, FamilyService, copyText
} from '../../../services/family.service';
import { PRODUCT_PRICES } from '../../../services/product-label';

/** Mirrors the backend's label limit (422 label_too_long). */
const MAX_LABEL = 60;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Badge colour per invitation status. */
const STATUS_COLOR: Record<FamilyInvitationStatus, string> = {
  pending:   'info',
  accepted:  'primary',
  purchased: 'success',
  expired:   'secondary',
  revoked:   'secondary',
  released:  'warning'
};

/**
 * "My Family" — the DNA Family SPECIAL slots of a buyer of the regular DNA &
 * Health SPECIAL (docs/globalhealth/DNA_FAMILY_SPEZIAL_MACHBARKEIT.md §4.2–4.6):
 * ten slots in total, each invitation a single-use link the member copies or
 * has mailed. The page shows ONLY what the main account itself entered (label,
 * e-mail) plus status and dates — never the family member's name, health or kit
 * data (§4.6); the backend sends nothing more.
 *
 * 403 family_not_qualified / 404 not_available render a friendly explanation;
 * the menu entry and the dashboard block are hidden in those cases anyway.
 */
@Component({
  selector: 'app-family-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, CardComponent, CardBodyComponent, ButtonDirective, BadgeComponent,
            SpinnerComponent],
  templateUrl: './family-page.component.html',
  styleUrls: ['./family-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FamilyPageComponent {
  private family     = inject(FamilyService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private route      = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private labelInput = viewChild<ElementRef<HTMLInputElement>>('labelInput');

  readonly maxLabel = MAX_LABEL;
  readonly fallbackPrice = PRODUCT_PRICES.dna_family_special.once;

  loading  = signal(true);
  /** 'not_qualified' | 'not_available' | 'error' — the page cannot be used. */
  blocked  = signal<'not_qualified' | 'not_available' | 'error' | null>(null);
  overview = signal<FamilyOverview | null>(null);
  invitations = computed(() => this.overview()?.invitations ?? []);

  // ----- Create -------------------------------------------------------------------
  label = '';
  email = '';
  private formTick = signal(0);
  onInput(): void { this.formTick.update(n => n + 1); }
  emailValid = computed(() => { this.formTick(); return !this.email.trim() || EMAIL.test(this.email.trim()); });
  labelValid = computed(() => { this.formTick(); return this.label.trim().length <= MAX_LABEL; });
  creating    = signal(false);
  createError = signal<string | null>(null);
  /** The invitation just created — its link is shown prominently with a copy button. */
  created     = signal<FamilyInvitation | null>(null);
  canCreate = computed(() =>
    !this.creating() && this.emailValid() && this.labelValid() && (this.overview()?.remaining ?? 0) > 0);

  // ----- Row actions --------------------------------------------------------------
  busyId    = signal<number | null>(null);
  rowError  = signal<{ id: number; message: string } | null>(null);
  rowNotice = signal<{ id: number; message: string } | null>(null);
  copiedId  = signal<number | null>(null);
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.load(this.route.snapshot.queryParamMap.get('invite') === '1');
  }

  load(focusForm = false): void {
    this.loading.set(true);
    this.family.overview()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: overview => {
          this.overview.set(overview);
          this.blocked.set(null);
          this.loading.set(false);
          if (focusForm) setTimeout(() => this.labelInput()?.nativeElement.focus());
        },
        error: err => {
          this.loading.set(false);
          this.blocked.set(err?.status === 403 ? 'not_qualified' : err?.status === 404 ? 'not_available' : 'error');
        }
      });
  }

  create(): void {
    this.onInput();
    if (!this.canCreate()) return;
    this.creating.set(true);
    this.createError.set(null);
    this.created.set(null);
    this.family.createInvitation({ label: this.label, email: this.email, language: this.lang() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.creating.set(false);
          this.created.set(res.invitation);
          this.label = '';
          this.email = '';
          this.onInput();
          this.load();
        },
        error: err => {
          this.creating.set(false);
          const code: string | undefined = err?.error?.code ?? err?.error?.error;
          const known = ['family_quota_exhausted', 'email_invalid', 'label_too_long', 'family_not_qualified'];
          this.createError.set(this.translate.translate(
            `family.errors.${code && known.includes(code) ? code : 'generic'}`));
          if (code === 'family_quota_exhausted') this.load();
        }
      });
  }

  revoke(inv: FamilyInvitation): void {
    if (!inv.revocable || this.busyId()) return;
    if (!window.confirm(this.translate.translate('family.revoke_confirm'))) return;
    this.busyId.set(inv.id);
    this.rowError.set(null);
    this.family.revoke(inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busyId.set(null);
          if (this.created()?.id === inv.id) this.created.set(null);
          this.load();
        },
        error: err => {
          this.busyId.set(null);
          const code = err?.error?.code ?? err?.error?.error;
          this.rowError.set({ id: inv.id, message: this.translate.translate(
            `family.errors.${code === 'not_revocable' ? 'not_revocable' : 'generic'}`) });
          if (code === 'not_revocable') this.load();
        }
      });
  }

  resend(inv: FamilyInvitation): void {
    if (!inv.email || this.busyId()) return;
    this.busyId.set(inv.id);
    this.rowError.set(null);
    this.rowNotice.set(null);
    this.family.resend(inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busyId.set(null);
          this.rowNotice.set({ id: inv.id, message: this.translate.translate('family.resent') });
        },
        error: err => {
          this.busyId.set(null);
          const code = err?.error?.code ?? err?.error?.error;
          this.rowError.set({ id: inv.id, message: this.translate.translate(
            `family.errors.${code === 'no_email' ? 'no_email' : 'generic'}`) });
        }
      });
  }

  copy(inv: FamilyInvitation): void {
    const link = this.linkOf(inv);
    if (!link) return;
    copyText(link).then(ok => {
      if (!ok) return;
      this.copiedId.set(inv.id);
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.copiedId.set(null), 2000);
    });
  }

  /** The shareable link, made absolute when the server sends a path. */
  linkOf(inv: FamilyInvitation | null): string | null {
    const link = inv?.link?.trim();
    if (!link) return null;
    return link.startsWith('/') ? `${window.location.origin}${link}` : link;
  }

  /** Only open invitations carry a usable link. */
  hasLink(inv: FamilyInvitation): boolean {
    return (inv.status === 'pending' || inv.status === 'accepted') && !!this.linkOf(inv);
  }

  statusColor(status: FamilyInvitationStatus): string {
    return STATUS_COLOR[status] ?? 'secondary';
  }

  price(): number | string {
    return this.overview()?.price ?? this.fallbackPrice;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(this.lang(), { year: 'numeric', month: 'short', day: 'numeric' });
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }
}
